import {Dispatch, useMemo, useState} from 'react'
import {Column} from 'react-table'
import {KTCard, KTSVG} from '../../../../../../../../_metronic/helpers'
import {KTable, KTableHeader, KTableLoading} from '../../../../../../components/UI'
import {CensusHistoryResponse} from '../../../../model/census.model'
import Chart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'
import moment from 'moment'
import 'moment/locale/es'
import clsx from 'clsx'
import html2canvas from 'html2canvas';

moment.locale('es')

interface CensusHistoryTableProps {
  data: CensusHistoryResponse[]
  loading: boolean
  setLoading: Dispatch<React.SetStateAction<boolean>>
  selectedCensus?: CensusHistoryResponse | null
  setSelectedCensus?: Dispatch<React.SetStateAction<CensusHistoryResponse | null>>
}

export const CensusHistoryTable = ({
  data,
  loading,
  setLoading,
  selectedCensus,
  setSelectedCensus,
}: CensusHistoryTableProps) => {

  const optionsChartCensusHistory: ApexOptions = useMemo(()=>({
    series: [],
    chart: {
      toolbar: {
        show: true,
        tools: {
          download: false,
        },
        customIcons: [
          {
            icon: '<span style="cursor:pointer;">📷</span>',
            index: 0,
            title: 'Descargar PNG',
            click: () => downloadPNG(),
          },
        ],
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 10,
        columnWidth: '30%',
        borderRadiusApplication: 'end', // 'around', 'end'
        borderRadiusWhenStacked: 'last', // 'all', 'last'
        dataLabels: {
          total: {
            enabled: true,
            style: {
              fontSize: '13px',
              fontWeight: 900,
            },
          },
        },
      },
    },
    xaxis: {
      type: 'category',
      categories: [''],
    },
    fill: {
      opacity: 1,
    },
    legend: {
      position: 'right',
      offsetY: 40,
    },
  }), [selectedCensus])

  const [chartData, setChartData] = useState<ApexOptions>(optionsChartCensusHistory)
  
  const [open, setOpen] = useState(false);

  const handleShowChart = (data: CensusHistoryResponse) => {
    setSelectedCensus(data)
    setChartData((prev) => ({
      ...prev,
      series: [
        {
          name: 'Camas Tendidas',
          data: [data.CamasTendidas || 0],
        },
        {
          name: 'Camas Destendidas',
          data: [data.CamasDestendidas || 0],
          color: '#FF4560',
        },
        {
          name: 'Camas Pendientes',
          data: [data.CamasPendientes || 0],
        },
      ],
    }))
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
    }, 500)
  }

  const downloadPNG = async () => {
    const element = document.getElementById('chart-export');
    if (!element) return;
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
    });

    const image = canvas.toDataURL('image/png');

    const link = document.createElement('a');
    link.href = image;
    link.download = 'censo.png';
    link.click();
  };

  const columns: Column<CensusHistoryResponse>[] = useMemo(
    () => [
      {
        Header: (props) => <KTableHeader tableProps={props} title='Campamento' />,
        accessor: 'Campamento',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Fecha Censo' />,
        accessor: 'FechaCenso',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Módulo' />,
        accessor: 'Modulo',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Camas Totales' />,
        accessor: 'CamasTotales',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Camas Tendidas' />,
        accessor: 'CamasTendidas',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Camas Destendidas' />,
        accessor: 'CamasDestendidas',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Camas Pendientes' />,
        accessor: 'CamasPendientes',
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Estado' name='Estado' />,
        accessor: 'Estado',
        Cell: ({...props}) => (
          <div className='flex '>
            <span
              className={clsx(
                'badge fs-6',
                props.row.original.Estado === 'PENDIENTE'
                  ? 'badge-danger'
                  : props.row.original.Estado === 'COMPLETADO'
                  ? 'badge-success'
                  : 'badge-warning',
              )}
            >
              {props.row.original.Estado}
            </span>
          </div>
        ),
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Ver Gráfico' />,
        id: 'actions',
        Cell: ({...props}) => (
          <div
            className='d-flex justify-content-center flex-shrink-0'
            onClick={() => handleShowChart(props.row.original)}
          >
            <a href='#' className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'>
              <KTSVG path='/media/icons/duotune/general/gen055.svg' className='svg-icon-3' />
            </a>
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <>
      <style>
        {`
          .apexcharts-menu-item.exportSVG,
          .apexcharts-menu-item.exportCSV {
            display: none;
          }
          .dropdown-item:hover {
            background: #f8f9fa;
          }
        `}
      </style>
      <KTCard shadow className='m-5'>
      {loading ? <KTableLoading title='Cargando...' /> : null}
      <KTable data={data} columns={columns as any} showPagination={false} />
        {selectedCensus && (
          <>
            <div className="d-flex justify-content-end mb-2">
              <div style={{ position: 'relative' }}>
                <button
                  className="btn btn-light"
                  onClick={() => setOpen(!open)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" d="M0 0h24v24H0V0z"></path><path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" fill="#6e8192"></path></svg>
                  <b>{'  '}Exportar</b>
                </button>
                {open && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      background: '#fff',
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                      zIndex: 9999,
                      minWidth: '130px',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                      padding: '10px'
                    }}
                  >
                    <button className="dropdown-item" onClick={downloadPNG}>
                      Descargar PNG
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="d-flex justify-content-center">
              <div id='chart-export'>
                <div className='d-flex flex-row justify-content-center'>
                  <div className='alert alert-warning' style={{fontSize: '20px'}}>
                    <b style={{color: 'black'}}>
                      {moment(selectedCensus?.FechaCenso, 'MMM DD YYYY hh:mmA', 'en')
                        .locale('es')
                        .format('D [de] MMMM [de] YYYY h:mm A')}
                    </b>
                  </div>
                </div>
                <Chart
                  options={chartData}
                  series={chartData.series}
                  type='bar'
                  height={450}
                  width={500}
                />
              </div>
            </div>
          </>
        )}
      </KTCard>
    </>
  )
}
